import { ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Row,
  Col,
  Form,
  Checkbox,
  Radio,
  Input,
  FormInstance,
  Typography,
  Skeleton,
} from 'antd';

import {
  CiliaSubAreaNameTranslator,
  CiliaSubAreas,
  WorkorderConfigurationCompany,
  WorkorderConfigurationCompanyFormValues,
} from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { Settings } from 'react-feather';
import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import useSession from '@/hooks/useSession';
import { useDefaultMaxLengthFieldError, useDisclosure } from '@/hooks';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import { isAppPortocar } from '@/index';
import { SubArea } from '@/core/domain/operational/SubAreas/SubAreas.types';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import { useGPStatusList } from '@/core/domain/visual-management/status/status.hooks';
import { GPStatusType } from '@/core/domain/visual-management/status/status.enums';
import ActionIcon from '@/components/_common/ActionIcon';
import StatusWorkOrderModal from '@/components/Settings/Profiles/StatusWorkOrder/StatusWorkOrderModal';
import CompleteActivityClosingWorkOrderModal from './CompleteActivityClosingWorkOrderModal';

const { Title } = Typography;

interface CompanyWorkorderSettingsFormProps {
  form: FormInstance<WorkorderConfigurationCompanyFormValues>;
  isLoading: boolean;
  extra?: ReactNode;
  workorderConfig?: WorkorderConfigurationCompany;
}

function CompanyWorkorderSettingsForm({
  form,
  isLoading,
  extra,
  workorderConfig,
}: CompanyWorkorderSettingsFormProps) {
  const session = useSession();

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const [statusUpdateModalOpen, statusUpdateModalAction] = useDisclosure();
  const [completeActivityModalOpen, completeActivityModalAction] =
    useDisclosure();

  const shouldCompleteActivityByClosingWO =
    workorderConfig?.completeActivityByClosingWorkOrder ?? false;

  const statusList = useGPStatusList(shouldCompleteActivityByClosingWO, {
    statusTypes: GPStatusType.Completed,
  });

  const shouldOpenCompleteActivityModal =
    statusList.isSuccess &&
    !statusList.data?.length &&
    shouldCompleteActivityByClosingWO;

  useEffect(() => {
    if (shouldOpenCompleteActivityModal) completeActivityModalAction.open();
  }, [statusList.dataUpdatedAt, shouldOpenCompleteActivityModal]);

  return (
    <Form layout="horizontal" form={form} name="companyBudget">
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <Title level={3}>
          <FormattedMessage id="companies.settings.form.budget" />
        </Title>
        {extra && <div className="actions">{extra}</div>}
      </div>

      <Skeleton active loading={isLoading} paragraph={{ rows: 10 }}>
        <Row gutter={[16, 8]}>
          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="profitMargin"
              label={
                <FormattedMessage id="companies.settings.form.marginRef" />
              }
              initialValue={0}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="maxPartDiscount"
              label={
                <FormattedMessage id="companies.settings.form.pieceDiscount" />
              }
              initialValue={0}
            >
              <InputDecimal max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="maxServiceDiscount"
              label={
                <FormattedMessage id="companies.settings.form.serviceDiscount" />
              }
              initialValue={0}
            >
              <InputDecimal max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} style={{ marginBottom: 24 }}>
            <Row gutter={[12, 12]}>
              <Col span={24} md={12} xl={8}>
                <Form.Item name="isKmRequired" valuePropName="checked" noStyle>
                  <Checkbox>
                    <FormattedMessage id="companies.settings.form.mandatoryKm" />
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={24} md={12} xl={8}>
                <Form.Item name="prisma" valuePropName="checked" noStyle>
                  <Checkbox>
                    <FormattedMessage id="companies.settings.form.prism" />
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={24} md={12} xl={8}>
                <Form.Item name="tankLevel" valuePropName="checked" noStyle>
                  <Checkbox>
                    <FormattedMessage id="companies.settings.form.tankLevel" />
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={24} md={12} xl={8}>
                <Form.Item
                  name="seeIndicatedBy"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Exibir campos de indicação de corretor</Checkbox>
                </Form.Item>
              </Col>

              <Col span={24} md={12} xl={8}>
                <Form.Item
                  name="shareObservation"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>
                    Vincular observações de orçamentos e cards
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Title level={4}>
              <FormattedMessage id="companies.settings.form.stock" />
            </Title>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="isAutomaticStock"
              valuePropName="checked"
              initialValue={true}
            >
              <Checkbox disabled={session?.module !== 'MASTER'}>
                <FormattedMessage id="companies.settings.form.stockAuto" />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} md={12} xl={8}>
            <Form.Item
              name="minimumStockNotification"
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Notificação de estoque mínimo</Checkbox>
            </Form.Item>
          </Col>

          <Col sm={24}>
            <Title level={4}>
              <FormattedMessage id="companies.settings.form.generateFinancial" />
            </Title>
          </Col>

          <Col sm={24}>
            <Form.Item name="isInstallmentFree" labelAlign="left">
              <Radio.Group>
                <Radio value="true">
                  <FormattedMessage id="companies.settings.form.withInstallments" />
                </Radio>
                <Radio value="false">
                  <FormattedMessage id="companies.settings.form.withInstallmentsPaymentConditions" />
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Title level={4}>
              <FormattedMessage id="companies.settings.form.tokens" />
            </Title>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="ciliaToken"
              label={
                <FormattedMessage id="companies.settings.form.tokenCilia" />
              }
              rules={[defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="audatexWebToken"
              label={
                <FormattedMessage id="companies.settings.form.tokenAudatexweb" />
              }
              rules={[defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>

        <Row gutter={[16, 8]}>
          <Col span={24} style={{ marginTop: '16px' }}>
            <Title level={4}>
              Criação de área por serviço no orçamento Cília
            </Title>
          </Col>

          <Col span={24} xl={16} xxl={12}>
            <Row gutter={[16, 8]}>
              {(form.getFieldValue('ciliaSubAreas') || []).map(
                (ciliaSubArea: CiliaSubAreas, index: number) => (
                  <>
                    <Form.Item
                      hidden
                      name={['ciliaSubAreas', index, 'ciliaSubAreaName']}
                    />

                    <Col span={24} sm={12}>
                      <FloatingFormItem
                        name={['ciliaSubAreas', index, 'subAreaId']}
                        label={`Área de ${
                          CiliaSubAreaNameTranslator[
                            ciliaSubArea?.ciliaSubAreaName
                          ]
                        }`}
                      >
                        <AreasInfiniteSelectField
                          allowClear
                          queryFilter={{ active: true }}
                        />
                      </FloatingFormItem>
                    </Col>
                  </>
                ),
              )}
            </Row>
          </Col>
        </Row>

        <Row gutter={[16, 8]}>
          <Col span={24} style={{ marginTop: '16px' }}>
            <Title level={4}>
              <FormattedMessage id="companies.settings.form.pieceCreate" />
            </Title>
          </Col>

          <Col span={24} xl={16} xxl={12}>
            <Row gutter={[16, 8]}>
              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partPaint', 'costPerHour']}
                  initialValue={0}
                  label={
                    <FormattedMessage id="companies.settings.form.paintingHourValue" />
                  }
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>

              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partPaint', 'subArea', 'id']}
                  label={
                    <FormattedMessage id="companies.settings.form.paintingArea" />
                  }
                >
                  <AreasInfiniteSelectField
                    subAreaSelected={
                      workorderConfig?.partPaint?.subArea as SubArea
                    }
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partChange', 'costPerHour']}
                  initialValue={0}
                  label={
                    <FormattedMessage id="companies.settings.form.changeHourValue" />
                  }
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>

              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partChange', 'subArea', 'id']}
                  label={
                    <FormattedMessage id="companies.settings.form.changeArea" />
                  }
                >
                  <AreasInfiniteSelectField
                    subAreaSelected={
                      workorderConfig?.partChange?.subArea as SubArea
                    }
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partRecuperation', 'costPerHour']}
                  initialValue={0}
                  label={
                    <FormattedMessage id="companies.settings.form.repairHourValue" />
                  }
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>

              <Col span={24} sm={12}>
                <FloatingFormItem
                  name={['partRecuperation', 'subArea', 'id']}
                  label={
                    <FormattedMessage id="companies.settings.form.repairArea" />
                  }
                >
                  <AreasInfiniteSelectField
                    subAreaSelected={
                      workorderConfig?.partRecuperation?.subArea as SubArea
                    }
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          {!isAppPortocar && (
            <>
              <Col span={24}>
                <Title level={4}>Bosch</Title>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="boschChecklist"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>
                    <FormattedMessage id="companies.settings.form.boschCheckList" />
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="alwaysSendWorkOrdersToMyBcs"
                  valuePropName="checked"
                >
                  <Checkbox>
                    <FormattedMessage id="companies.settings.form.alwaysSendWorkOrdersToMyBcs" />
                  </Checkbox>
                </Form.Item>
              </Col>
            </>
          )}

          <Col span={24}>
            <Title level={4}>Gestão de pátio</Title>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="alwaysGenerateActivityByWorkOrder"
              valuePropName="checked"
              style={{ marginTop: '8px' }}
              noStyle
            >
              <Checkbox>
                <FormattedMessage id="companies.settings.form.alwaysGenerateActivityByWorkOrder" />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} style={{ marginBottom: 12 }}>
            <Group gap={5}>
              <Form.Item
                name="completeActivityByClosingWorkOrder"
                valuePropName="checked"
                noStyle
              >
                <Checkbox>
                  Mover card automaticamente para o status “Concluído” quando a
                  OS for fechada/cancelada
                </Checkbox>
              </Form.Item>

              {workorderConfig?.completeActivityByClosingWorkOrder && (
                <ActionIcon
                  type="button"
                  size={18}
                  onClick={
                    !statusList.data?.length
                      ? completeActivityModalAction.open
                      : statusUpdateModalAction.open
                  }
                >
                  <Settings />
                </ActionIcon>
              )}
            </Group>
          </Col>

          <Col span={24}>
            <Title level={4}>Folha do mecânico</Title>
          </Col>

          <Col span={24}>
            <Form.Item name="isMechanicSheetAutoFill" valuePropName="checked">
              <Checkbox>
                <FormattedMessage id="companies.settings.form.isMechanicSheetAutoFill" />
              </Checkbox>
            </Form.Item>
          </Col>

          {isAppPortocar && (
            <>
              <Col span={24}>
                <Title level={4}>Orçamentista padrão de bateria</Title>
              </Col>

              <Col span={24} md={12}>
                <FloatingFormItem
                  name="portoBatteryDefaultEstimatorId"
                  label="Orçamentista"
                  style={{ marginTop: '8px' }}
                >
                  <EmployeesInfiniteSelectField
                    queryFilter={{
                      isActive: true,
                      estimator: true,
                      companyId: workorderConfig?.company.id,
                    }}
                    allowClear
                  />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Skeleton>

      <StatusWorkOrderModal
        statusSelected={statusList.data?.at(0)}
        open={statusUpdateModalOpen}
        onCancel={statusUpdateModalAction.close}
        completeActivity
      />

      <CompleteActivityClosingWorkOrderModal
        open={completeActivityModalOpen}
        onCancel={completeActivityModalAction.close}
      />
    </Form>
  );
}

export default CompanyWorkorderSettingsForm;
